import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import { openWhatsApp } from "@/lib/whatsappTemplates";

/**
 * Concierge-facing WhatsApp send preview.
 *
 * Renders a small green pill button that opens a popover with the
 * pre-templated message. The concierge can tweak the copy inline
 * before firing the deep-link. Includes a one-click "reset to
 * template" affordance.
 *
 * Props:
 *   phone            — E.164 customer phone (button disabled if empty)
 *   defaultMessage   — starting template text (from buildCrossSellMessage)
 *   serviceLabel     — human label shown in the popover title (e.g. "Boat Charter")
 *   customerName     — used only in the toast + button tooltip
 *   testIdSuffix     — data-testid suffix, defaults to serviceLabel-slug
 */
export function CrossSellWhatsAppButton({
  phone,
  defaultMessage,
  serviceLabel,
  customerName,
  testIdSuffix,
}) {
  const disabled = !phone;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(defaultMessage);

  // Keep the draft in sync if the parent's template changes (e.g. selected booking swap).
  useEffect(() => {
    setDraft(defaultMessage);
  }, [defaultMessage]);

  const suffix = testIdSuffix || (serviceLabel || "cross-sell").toLowerCase().replace(/\s+/g, "-");

  const send = () => {
    const trimmed = (draft || "").trim();
    if (!trimmed) {
      toast.error("Message can't be empty");
      return;
    }
    openWhatsApp(phone, trimmed);
    toast.success(`WhatsApp opened · ${serviceLabel}`, {
      description: `Mark as Booked once ${customerName?.split(" ")[0] || "the customer"} confirms.`,
    });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`cross-sell-whatsapp-${suffix}`}
          disabled={disabled}
          title={
            disabled
              ? "No phone number on file"
              : `Preview & send WhatsApp about ${serviceLabel}`
          }
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-[10px] uppercase tracking-widest hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <MessageCircle className="w-3 h-3" />
          <span className="hidden sm:inline">WhatsApp</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="left"
        sideOffset={8}
        className="w-[360px] sm:w-[420px] p-0 bg-zinc-950 border border-white/10 shadow-2xl"
        data-testid={`cross-sell-whatsapp-popover-${suffix}`}
      >
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-emerald-300/80">
              WhatsApp preview
            </p>
            <p className="text-sm text-zinc-100 font-light mt-0.5">
              {serviceLabel}
              {customerName ? (
                <span className="text-zinc-500"> · {customerName.split(" ")[0]}</span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDraft(defaultMessage)}
            title="Reset to template"
            data-testid={`cross-sell-whatsapp-reset-${suffix}`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200 transition"
          >
            <RotateCcw className="w-3 h-3" />
            Reset
          </button>
        </div>
        <div className="p-4 space-y-3">
          <Textarea
            data-testid={`cross-sell-whatsapp-textarea-${suffix}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={7}
            className="w-full text-sm bg-zinc-900 border-white/10 text-zinc-100 focus-visible:ring-emerald-500/40 resize-none leading-relaxed"
          />
          <div className="flex items-center justify-between text-[10px] text-zinc-500">
            <span>{draft.length} chars</span>
            <span>Opens WhatsApp in a new tab</span>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[11px] uppercase tracking-widest text-zinc-400 hover:text-zinc-200 px-3 py-1.5"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid={`cross-sell-whatsapp-send-${suffix}`}
              onClick={send}
              className="inline-flex items-center gap-1.5 h-8 px-4 rounded-full border border-emerald-500/50 bg-emerald-500/20 text-emerald-100 text-[11px] uppercase tracking-widest hover:bg-emerald-500/30 transition"
            >
              <Send className="w-3 h-3" />
              Send via WhatsApp
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
